import { dequal } from 'dequal'

import { format } from '@sequencehq/utils/dates'

import {
  AggregatedLineItemGroup,
  AggregatedLineItemGroupMutation
} from 'Invoices/types'
import { FormState } from './state.atoms'
import { DATE_FORMAT_API } from 'Invoices/constants'
import { servicePeriodStringToDates } from '../../common/utils/servicePeriodStringToDates'

export const lineItemGroupToFormState = (
  lineItemGroup: AggregatedLineItemGroup
): FormState => {
  const firstLineItem = lineItemGroup.lineItems.at(0)

  return {
    title: lineItemGroup.title,
    descriptionEnabled: !!lineItemGroup.description,
    description: lineItemGroup.description ?? '',
    taxCategoryId: lineItemGroup.taxCategory?.id ?? '',
    servicePeriod: firstLineItem?.servicePeriod
      ? servicePeriodStringToDates(firstLineItem.servicePeriod)
      : undefined,
    revenueRecognitionMethod: firstLineItem?.revenueRecognitionMethod,
    revenueClassification: firstLineItem?.revenueClassification,
    ...getExternalIdsFormState(lineItemGroup.lineItems)
  }
}

const getExternalIdsFormState = (
  lineItems: AggregatedLineItemGroup['lineItems']
): Pick<FormState, 'externalIds' | 'externalIdsEditable'> => {
  if (lineItems.length === 0) {
    return {
      externalIds: [],
      externalIdsEditable: false
    }
  }

  const { externalIds: firstExternalIds } = lineItems[0]

  const allExternalIdsEqual = lineItems.every(({ externalIds }) =>
    dequal(externalIds, firstExternalIds)
  )

  return {
    externalIdsEditable: allExternalIdsEqual,
    externalIds: allExternalIdsEqual && firstExternalIds ? firstExternalIds : []
  }
}

export const formStateToLineItemGroup = ({
  formState,
  lineItemGroup
}: {
  formState: FormState
  lineItemGroup: AggregatedLineItemGroup
}): AggregatedLineItemGroupMutation => {
  const servicePeriod = formState.servicePeriod
    ? {
        start: format(formState.servicePeriod.start, DATE_FORMAT_API),
        endInclusive: format(
          formState.servicePeriod.endInclusive,
          DATE_FORMAT_API
        )
      }
    : undefined

  return {
    id: lineItemGroup.id,
    title: formState.title,
    taxCategoryId: formState.taxCategoryId,
    description: formState.descriptionEnabled ? formState.description : '',
    revenueRecognitionMethod: formState.revenueRecognitionMethod,
    revenueClassification: formState.revenueClassification,
    servicePeriod,
    lineItems: lineItemGroup.lineItems.map(lineItem => ({
      ...lineItem,
      servicePeriod: servicePeriod
        ? {
            /**
             * servicePeriod attribute names are different for lineItems vs lineItemGroups
             */
            startDate: servicePeriod.start,
            endDate: servicePeriod.endInclusive
          }
        : undefined,
      revenueRecognitionMethod: formState.revenueRecognitionMethod,
      revenueClassification: formState.revenueClassification,
      externalIds: formState.externalIdsEditable
        ? formState.externalIds
        : lineItem.externalIds
    }))
  }
}
